import { useSelector } from 'react-redux';

/**
 * useManagePermittedRoles hook determines if the currently logged in user
 * has permission to perform an action based on a permission name and required entity IDs.
 *
 * @param {string} permissionName - The permission key from the remote config.
 * @param {Object} [roleMapping={}] - (Optional) An object where each key is a role ID
 *   and its value is an array of entity IDs the user is allowed to access.
 *
 * NOTE: Each value inside `roleMapping` **must be an array**, e.g., { 103: [35], 105: [10, 11] }.
 *
 * Example roles:
 * - 101 → orgID
 * - 103 → siteID
 * - 104 → floorID
 * - 105 → teamID
 *
 * @returns {boolean} True if the user is permitted, otherwise false.
 *  Note:
 * - If `entityID` is `false` only the role is checked**.
 *   In that case, you **do NOT need to pass `roleMapping`**.
 *
 * Example:
 *
 *  Only role check (entityID: false)
 * const canAddOrg = useManagePermittedRoles('addOrg');
 *
 *  Role + entity check (entityID: true)
 * const canUpdateSite = useManagePermittedRoles('updateSite', { 103: [35] });
 *
 */
const useManagePermittedRoles = (permissionName, roleMapping = {}) => {
	// Get the remote config for permission settings
	const { remoteConfig } = useSelector((state) => state.FbRemoteConfig);
	const { managePermittedRolesConfig: config } = remoteConfig;

	// Get the current user's roles and userTypeID from the store
	const { roles: userRoles = {} } = useSelector((state) => state.Auth.selectedProfile) || {};

	// Get permission configuration for the given permissionName
	const permissionConfig = config[permissionName];
	if (!permissionConfig) return false;

	const { roles: permittedRoles = [], entityID: shouldCheckEntity = true } = permissionConfig;

	// Developer role (201) bypasses all checks and has global access.
	if (Object.keys(userRoles).map(Number).includes(201)) return true;

	// If the Entity Check is not required then check if the user has the permission to
	// view the component based on the roles only
	// If the user has the role then return true
	if (!shouldCheckEntity) {
		const matchedRoles = Object.keys(userRoles)
			.map(Number)
			.filter((role) => permittedRoles.includes(role));
		if (matchedRoles.length > 0) return true;
	}

	// if the Entity Check is required then check if the user has the permission to
	// view the component based on the roles and entity ID
	if (shouldCheckEntity) {
		/**
		 * This snippet builds an object (`rolesPermitted`) that maps each permitted role (from the `permittedRoles` array)
		 * to its corresponding entity ID provided in the hook options. Each entity ID is expected to be wrapped in an array
		 * (to enable the use of array methods like `.includes` later on). The process involves:
		 *
		 * 1. Creating a mapping (`roleMapping`) of role IDs to entity IDs (e.g., role 101 maps to `orgID`, role 105 maps to `teamID`, etc.).
		 * 2. Filtering the `permittedRoles` to include only those roles that have a corresponding entity value in `roleMapping`
		 *    that is an array.
		 * 3. Converting the filtered role-entity pairs into an object (`rolesPermitted`) using `Object.fromEntries`.
		 *
		 * This object is then used to compare against the user's roles to determine if they have the necessary permissions.
		 *
		 */

		// Filter permitted roles to include only those with a corresponding entity value in `roleMapping`.
		const rolesPermitted = Object.fromEntries(
			permittedRoles.filter((role) => Array.isArray(roleMapping[role])).map((role) => [role, roleMapping[role]])
		);

		const hasPermission = Object.keys(userRoles).some((role) => {
			// Check if the role exists in the permitted roles.
			if (rolesPermitted[role]) {
				// For this role, check if any of the user's entity IDs match the permitted entity IDs.
				return userRoles[role].some((entityId) => rolesPermitted[role].includes(entityId));
			}
			return false;
		});

		return hasPermission;
	}

	// if the user does not have the permission to view the component then return false
	return false;
};

const usePermittedUpdatePerson = (personID) => {
	const roles = useSelector((state) => state.Auth.selectedProfile.roles) || {};
	const { personID: permittedPersonID } = useSelector((state) => state.Auth.selectedProfile);
	let globalPermission = false;

	// Developers Or OrgAdmin can view Update Person components
	if (Object.keys(roles).map(Number).includes(201) || Object.keys(roles).map(Number).includes(101)) {
		globalPermission = true;
	}

	return permittedPersonID === personID || globalPermission;
};

export { usePermittedUpdatePerson };

export default useManagePermittedRoles;
